import React, { useEffect, useState, type PropsWithChildren } from "react";

import { useAdoptedBudgetMix } from "@features/mmm/hooks/use-adopted-budget-mix";
import {
  FETCH_LIVE_FORECAST_TRACKING_DATA,
  FETCH_PERFORMANCE_TRACKING_DATA,
} from "@gql/mmm-performance-tracking";
import { useFeatureFlags, useNorthbeamLazyQuery } from "@hooks/http";
import {
  type FetchLiveForecastTrackingData,
  type FetchLiveForecastTrackingDataVariables,
  type FetchPerformanceTrackingData,
  type FetchPerformanceTrackingDataVariables,
} from "@north-beam/nb-client-side";
import { useClientKpis } from "@pages/mmm/hooks/use-client-kpis";
import { useClientSettingData } from "@pages/mmm/hooks/use-client-setting-data";
import { useClientSettings } from "@pages/mmm/hooks/use-client-settings";
import { useSelectedCycle } from "@pages/mmm/hooks/use-selected-cycle";

export interface PerformanceTrackingContextType {
  data: FetchPerformanceTrackingData | undefined;
  liveForecastData: FetchLiveForecastTrackingData | undefined;
  loading: boolean;
}

const PerformanceTrackingContext =
  React.createContext<PerformanceTrackingContextType>({
    data: undefined,
    liveForecastData: undefined,
    loading: false,
  });

export const usePerformanceTrackingData = () => {
  const context = React.useContext(PerformanceTrackingContext);
  if (context === undefined) {
    throw new Error(
      "usePerformanceTrackingData must be used within a PerformanceTrackingProvider",
    );
  }
  return context;
};

export function PerformanceTrackingProvider({
  children,
}: PropsWithChildren<unknown>) {
  const { selectedCycle } = useSelectedCycle();

  const { adoptedClientSetting } = useClientSettings();
  const { kpi: adoptedClientSettingKpi } =
    useClientSettingData(adoptedClientSetting);
  const { adoptedBudgetMix } = useAdoptedBudgetMix();
  const { kpis: clientKpis } = useClientKpis();
  const [loadedCycleId, setLoadedCycleId] = useState<string | null>(null);
  const { hasFeatureFlag } = useFeatureFlags();
  const enableMMMLiveForecasting = hasFeatureFlag("enableMMMLiveForecasting");

  const {
    loadData,
    data: { data, loading },
  } = useNorthbeamLazyQuery<
    FetchPerformanceTrackingData,
    FetchPerformanceTrackingDataVariables
  >(FETCH_PERFORMANCE_TRACKING_DATA, {
    fetchPolicy: "network-only",
  });

  const {
    loadData: loadForecastData,
    data: { data: liveForecastData, loading: liveForecastLoading },
  } = useNorthbeamLazyQuery<
    FetchLiveForecastTrackingData,
    FetchLiveForecastTrackingDataVariables
  >(FETCH_LIVE_FORECAST_TRACKING_DATA, { errorPolicy: "ignore" });

  useEffect(() => {
    const cycleId = selectedCycle?.id;
    let kpiId = adoptedClientSettingKpi?.id;

    if (!kpiId) {
      kpiId = clientKpis.find(
        (e) => e.kpi_name === adoptedBudgetMix?.data.kpi_name,
      )?.id;
    }

    if (kpiId && cycleId && adoptedBudgetMix?.id) {
      setLoadedCycleId(cycleId);
      loadData({
        variables: { cycleId, kpiId },
      });
      if (enableMMMLiveForecasting) {
        loadForecastData({
          variables: {
            cycleId,
            adoptedMixId: `${adoptedBudgetMix?.id}`,
          },
        });
      }
    }
  }, [
    adoptedClientSettingKpi?.id,
    loadData,
    loadForecastData,
    selectedCycle?.id,
    clientKpis,
    adoptedBudgetMix?.data.kpi_name,
    adoptedBudgetMix?.id,
    enableMMMLiveForecasting,
  ]);

  const isOldData = selectedCycle?.id !== loadedCycleId;

  return (
    <PerformanceTrackingContext.Provider
      value={{
        data,
        liveForecastData,
        loading: loading || isOldData || liveForecastLoading,
      }}
    >
      {children}
    </PerformanceTrackingContext.Provider>
  );
}
